<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  canCheckIn: { type: Boolean, default: false },
  canFinalizeOutcome: { type: Boolean, default: false },
  checkInSaving: { type: Boolean, default: false },
  feedbackLink: { type: String, default: '' },
  feedbackTokenSaving: { type: Boolean, default: false },
  outcomeDialogOpen: { type: Boolean, default: false },
  outcomeError: { type: String, default: '' },
  outcomeSaving: { type: Boolean, default: false },
  visit: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'check-in',
  'copy-feedback-link',
  'finalizeOutcome',
  'issue-feedback-token',
  'update:outcomeDialogOpen',
]);

const TEXT = Object.freeze({
  awaitingCheckIn: 'Aguardando check-in',
  cancel: 'Cancelar',
  checkInConfirmed: 'Check-in confirmado',
  confirmed: 'Confirmado',
  copy: 'Copiar',
  feedback: 'Feedback',
  feedbackReady: 'Link pronto para compartilhar',
  feedbackWaiting: 'Envie um link ao visitante',
  generateLink: 'Gerar link',
  nextAction: 'Próxima ação',
  outcome: 'Resultado',
  outcomeRecorded: 'Resultado registrado',
  outcomeWaiting: 'Registre o resultado comercial',
  pending: 'Pendente',
  presence: 'Presença',
  reason: 'Observação',
  reasonPlaceholder: 'Adicione um contexto para a próxima ação',
  registerOutcome: 'Registrar resultado',
  registerPresence: 'Registrar presença',
  saveOutcome: 'Salvar resultado',
  title: 'Operação da visita',
  outcomeDialogTitle: 'Registrar resultado da visita',
});

const outcomeOptions = Object.freeze([
  { value: 'interessado', label: 'Interessado' },
  { value: 'nao_interessado', label: 'Não interessado' },
  { value: 'quer_revisita', label: 'Quer revisitar' },
  { value: 'fara_proposta', label: 'Fará proposta' },
  { value: 'imovel_incompativel', label: 'Imóvel incompatível' },
  { value: 'acesso_falhou', label: 'Acesso falhou' },
  { value: 'acompanhamento_necessario', label: 'Acompanhamento necessário' },
]);

const nextActionOptions = Object.freeze([
  { value: 'none', label: 'Sem próxima ação' },
  { value: 'create_offer', label: 'Criar proposta' },
  { value: 'follow_up', label: 'Fazer acompanhamento' },
  { value: 'schedule_revisit', label: 'Agendar revisita' },
  { value: 'suppress_recommendations', label: 'Ocultar recomendações' },
  { value: 'fix_listing', label: 'Corrigir anúncio' },
  { value: 'resolve_access', label: 'Resolver acesso' },
]);

const selectedOutcome = ref('');
const nextAction = ref('none');
const reason = ref('');

const outcome = computed(
  () => props.visit.visitOutcome || props.visit.visit_outcome || null
);
const checkInDone = computed(() =>
  Boolean(props.visit.checkinDone || props.visit.checkin_done)
);
const outcomeLabel = computed(
  () =>
    outcomeOptions.find(option => option.value === outcome.value?.outcome)
      ?.label || TEXT.outcomeRecorded
);
const checkInLabel = computed(() =>
  checkInDone.value ? TEXT.checkInConfirmed : TEXT.awaitingCheckIn
);
const feedbackLabel = computed(() =>
  props.feedbackLink ? TEXT.feedbackReady : TEXT.feedbackWaiting
);
const outcomeDescription = computed(() =>
  outcome.value ? outcomeLabel.value : TEXT.outcomeWaiting
);
const canSubmitOutcome = computed(
  () => Boolean(selectedOutcome.value) && !props.outcomeSaving
);

const resetDraft = () => {
  selectedOutcome.value = '';
  nextAction.value = 'none';
  reason.value = '';
};

watch(
  () => props.outcomeDialogOpen,
  open => open && resetDraft()
);

const closeDialog = () => emit('update:outcomeDialogOpen', false);
const submitOutcome = () => {
  if (!canSubmitOutcome.value) return;
  emit('finalizeOutcome', {
    outcome: selectedOutcome.value,
    next_action: nextAction.value,
    reason: reason.value.trim(),
  });
};
</script>

<template>
  <LaisCard :title="TEXT.title" prefix-icon="i-lucide-clipboard-check">
    <div class="grid gap-3">
      <section
        class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex min-w-0 gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-full bg-violet-100 text-lais-primary"
            >
              <span class="i-lucide-map-pin-check size-4" aria-hidden="true" />
            </span>
            <div class="min-w-0">
              <p class="text-[13px] font-semibold text-lais-text">
                {{ TEXT.presence }}
              </p>
              <p class="mt-0.5 text-[12px] text-lais-text-muted">
                {{ checkInLabel }}
              </p>
            </div>
          </div>
          <span
            class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
            :class="
              checkInDone
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-violet-100 text-lais-primary'
            "
          >
            {{ checkInDone ? TEXT.confirmed : TEXT.pending }}
          </span>
        </div>
        <LaisPrimaryButton
          v-if="canCheckIn && !checkInDone"
          class="mt-3 w-full"
          size="sm"
          icon="i-lucide-map-pin-check"
          :is-loading="checkInSaving"
          @click="emit('check-in')"
        >
          {{ TEXT.registerPresence }}
        </LaisPrimaryButton>
      </section>

      <section
        class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-3"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex min-w-0 gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-full bg-violet-100 text-lais-primary"
            >
              <span class="i-lucide-sparkles size-4" aria-hidden="true" />
            </span>
            <div class="min-w-0">
              <p class="text-[13px] font-semibold text-lais-text">
                {{ TEXT.outcome }}
              </p>
              <p class="mt-0.5 text-[12px] text-lais-text-muted">
                {{ outcomeDescription }}
              </p>
            </div>
          </div>
          <span
            class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
            :class="
              outcome
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-violet-100 text-lais-primary'
            "
          >
            {{ outcome ? TEXT.outcomeRecorded : TEXT.pending }}
          </span>
        </div>
        <LaisPrimaryButton
          v-if="canFinalizeOutcome && !outcome"
          class="mt-3 w-full"
          size="sm"
          icon="i-lucide-flag"
          @click="emit('update:outcomeDialogOpen', true)"
        >
          {{ TEXT.registerOutcome }}
        </LaisPrimaryButton>
      </section>

      <section
        class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-3"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex min-w-0 gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-full bg-violet-100 text-lais-primary"
            >
              <span
                class="i-lucide-message-square-heart size-4"
                aria-hidden="true"
              />
            </span>
            <div class="min-w-0">
              <p class="text-[13px] font-semibold text-lais-text">
                {{ TEXT.feedback }}
              </p>
              <p class="mt-0.5 text-[12px] text-lais-text-muted">
                {{ feedbackLabel }}
              </p>
            </div>
          </div>
          <LaisPrimaryButton
            size="sm"
            variant="ghost"
            :is-loading="feedbackTokenSaving"
            :icon="feedbackLink ? 'i-lucide-copy' : 'i-lucide-link'"
            @click="
              feedbackLink
                ? emit('copy-feedback-link')
                : emit('issue-feedback-token')
            "
          >
            {{ feedbackLink ? TEXT.copy : TEXT.generateLink }}
          </LaisPrimaryButton>
        </div>
      </section>
    </div>

    <LaisModal
      :show="outcomeDialogOpen"
      :title="TEXT.outcomeDialogTitle"
      max-width-class="max-w-[560px]"
      @close="closeDialog"
    >
      <div class="grid gap-4">
        <div>
          <p class="text-[13px] font-semibold text-lais-text">
            {{ TEXT.outcome }}
          </p>
          <div class="mt-2 grid gap-2 sm:grid-cols-2">
            <button
              v-for="option in outcomeOptions"
              :key="option.value"
              type="button"
              class="rounded-lais-sm border px-3 py-2 text-left text-[12px] font-semibold transition-colors"
              :class="
                selectedOutcome === option.value
                  ? 'border-lais-primary bg-violet-50 text-lais-primary'
                  : 'border-lais-border text-lais-text-body'
              "
              @click="selectedOutcome = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <label class="grid gap-1.5 text-[13px] font-semibold text-lais-text">
          {{ TEXT.nextAction }}
          <select
            v-model="nextAction"
            class="min-h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text"
          >
            <option
              v-for="option in nextActionOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="grid gap-1.5 text-[13px] font-semibold text-lais-text">
          {{ TEXT.reason }}
          <textarea
            v-model="reason"
            rows="3"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[13px] font-normal text-lais-text"
            :placeholder="TEXT.reasonPlaceholder"
          />
        </label>

        <p
          v-if="outcomeError"
          class="rounded-lais-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
        >
          {{ outcomeError }}
        </p>
      </div>

      <template #footer>
        <div class="flex flex-wrap justify-end gap-2">
          <LaisPrimaryButton variant="ghost" @click="closeDialog">
            {{ TEXT.cancel }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-check"
            :disabled="!canSubmitOutcome"
            :is-loading="outcomeSaving"
            @click="submitOutcome"
          >
            {{ TEXT.saveOutcome }}
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>
  </LaisCard>
</template>
